import { useEffect, useRef } from 'react';
import { useStillFrameReadyRegistration } from '@/components/preview/still-frame-ready-context';

const videoStyle: React.CSSProperties = {
  maxWidth: '100%',
  maxHeight: '100%',
  width: 'auto',
  height: 'auto',
  objectFit: 'contain',
  display: 'block',
};

/** If play/decode never settles, still unblock Control readiness. */
const STILL_FRAME_VIDEO_SETTLE_TIMEOUT_MS = 2000;

function freezeVideo(video: HTMLVideoElement) {
  video.pause();
  try {
    if (video.currentTime !== 0) {
      video.currentTime = 0;
    }
  } catch {
    // Seek can fail before metadata is ready.
  }
}

/**
 * Shows the first decoded video frame and keeps playback frozen.
 * Used by Control Now/Next previews — not for interactive MediaPreview.
 */
export function StillFrameVideo({ url, onDuration }: { url: string; onDuration?: (seconds: number) => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const settleReady = useStillFrameReadyRegistration(true);
  const readySentRef = useRef(false);
  const onDurationRef = useRef(onDuration);

  useEffect(() => {
    onDurationRef.current = onDuration;
  }, [onDuration]);

  useEffect(() => {
    readySentRef.current = false;
  }, [url]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    let cancelled = false;
    let rafId = 0;

    const signalReady = () => {
      if (!settleReady || cancelled || readySentRef.current) {
        return;
      }
      readySentRef.current = true;
      settleReady();
    };

    const freeze = () => {
      if (cancelled) {
        return;
      }
      freezeVideo(video);
      if (Number.isFinite(video.duration) && video.duration > 0) {
        onDurationRef.current?.(video.duration);
      }
      signalReady();
    };

    /** Any play (including our decode kick) is stopped after one paint. */
    const onPlay = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        freeze();
      });
    };

    const onTimeUpdate = () => {
      // Only force-freeze if playback escaped; avoid re-entering freeze after settle.
      if (!video.paused && video.currentTime > 0.05) {
        freeze();
      }
    };

    const kickDecode = () => {
      if (cancelled) {
        return;
      }
      void video.play().catch(() => {
        freeze();
      });
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('error', signalReady);
    video.addEventListener('loadeddata', freeze);

    if (video.readyState >= 1) {
      kickDecode();
    } else {
      video.addEventListener('loadedmetadata', kickDecode, { once: true });
    }

    const timeoutId = window.setTimeout(signalReady, STILL_FRAME_VIDEO_SETTLE_TIMEOUT_MS);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('error', signalReady);
      video.removeEventListener('loadeddata', freeze);
      video.removeEventListener('loadedmetadata', kickDecode);
      freezeVideo(video);
    };
  }, [settleReady, url]);

  return (
    <video
      ref={videoRef}
      key={url}
      src={url}
      muted
      playsInline
      preload="metadata"
      controls={false}
      autoPlay={false}
      loop={false}
      style={videoStyle}
    />
  );
}
